/* eslint-disable react-hooks/exhaustive-deps */
import React, { ReactElement, useEffect, useState, useMemo } from 'react';
import { Row, Col, Form, Input, Modal, Radio, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { useForm } from 'antd/es/form/Form';
import LoanLayout from '../LoanLayout';
import { useLoan } from '../../stores/store';
import Loading from '../Loading';
import ButtonNav from '../../components/ButtonNav/ButtonNav';
import PlaceholderLabel from '../../components/PlaceholderLabel/PlaceholderLabel';
import { stepFinished } from '../../nav';
import ManualLabel from '../../components/ManualLabel/ManualLabel';
import { createSchemaValidator } from '../../validators/schemaValidator';
import { pickFields } from './meta';
import AppFormItem from '../components/AppFormItem';
import { Loan, LoanPurpose, LoanType, OwnershipType, SubSource, UseType } from '../../schema';
import { ColProps } from 'antd/lib/grid/col';
import useConfig from '../../stores/config';
import { ReactComponent as SelectArrowIcon } from '../../icons/select-arrow-icon.svg';
import { StepFooter } from '../components/StepFooter/StepFooter';
import { FF_FULL_APPLICATION_STYLING, isProduction } from '../../constants';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

const centerSmall = { xs: 24, sm: 24, md: 16, lg: 16, xl: 16 };
const centerSmallOffset = { xs: 0, sm: 0, md: 4, lg: 4, xl: 4 };
/**
 * @description This is a temporary flag to show the charter option on the details step.
 * It will be removed once the Feature Flag is created. Because we'll want this to be tied to the
 * feature flag. For now, it should be "off"/false in production.
 */
const SHOULD_SHOW_CHARTER_OPTION = !isProduction();
const schemaValidator = createSchemaValidator(pickFields('details'));

const setUserCanPickSubSource = (loan: Loan) => {
  if (loan) {
    const newValue = loan?.userCanPickSubsource || !loan?.subSource;
    // Only update if the value would actually change (avoid infinite renders)
    if (loan.userCanPickSubsource !== newValue) {
      runInAction(() => {
        loan.userCanPickSubsource = newValue;
      });
    }
  }
};

const DetailsStep = observer(() => {
  const loanStore = useLoan();
  const { loan } = loanStore;
  const [form] = useForm();
  const config = useConfig();
  const isNewFunnel = useFeatureFlag({
    flag: FF_FULL_APPLICATION_STYLING,
  });

  const [showCharterOptions, setShowCharterOptions] = useState(false);

  useEffect(() => {
    if (!loan) {
      return;
    }

    setUserCanPickSubSource(loan as Loan);

    // Set showCharterOptions based on current useType
    if (loan?.useType === UseType.charter) {
      setShowCharterOptions(true);
    }

    // If the useType is a specific charter type, show charter options
    if (loan?.useType === UseType.fullTimeCharter || loan?.useType === UseType.partTimeCharter) {
      setShowCharterOptions(true);
    }
  }, [loan?.id, loan?.useType]);

  if (!loan || loanStore.status === 'pending' || !config) {
    return <Loading />;
  }

  // Extract only the values we need to avoid MobX reactivity issues
  const stableLoanSource = loan?.source;
  const stableLoanPurpose = loan?.purpose;
  const stableLoanUserCanPickSubsource = loan?.userCanPickSubsource;
  const stableLoanSubSource = loan?.subSource;

  const { subSources, showSubsourceDropdown } = useMemo(() => {
    let calculatedSubSources: SubSource[] = null as unknown as SubSource[];
    let showDropdown = true;

    try {
      const source = config.getSource(stableLoanSource);

      calculatedSubSources = source.sub
        .filter((ss) => ss.products.includes(stableLoanPurpose))
        .filter((ss) => !ss.isReferral);

      if (
        (!stableLoanUserCanPickSubsource && stableLoanSubSource) ||
        stableLoanPurpose === LoanPurpose.boat
      ) {
        showDropdown = false;
      }
    } catch (e) {
      console.error(e);
    }

    if (!calculatedSubSources) {
      calculatedSubSources = config.sources[0].sub;
    }

    return { subSources: calculatedSubSources, showSubsourceDropdown: showDropdown };
  }, [
    config,
    stableLoanSource,
    stableLoanPurpose,
    stableLoanUserCanPickSubsource,
    stableLoanSubSource,
  ]);

  const update = async () => {
    const { subSource } = loan;
    if (subSource !== null && subSource !== undefined) {
      // store the label as well
      for (let i = 0; i < subSources.length; i++) {
        if (subSources[i].id === subSource) {
          loan.subSourceLabel = subSources[i].name;
        }
      }
    }
    loanStore.updateDetails();
    await stepFinished();
  };

  const handleChange = (changed: any, values: any) => {
    // Handle charter selection logic
    if (changed.useType === UseType.charter) {
      setShowCharterOptions(true);
    } else if (changed.useType && changed.useType !== UseType.charter) {
      setShowCharterOptions(false);
      // Update the actual loan.useType when a non-charter option is selected (Pleasure, Live-aboard, etc.)
      loan.useType = changed.useType;
    }

    // Handle charter type selection
    if (changed.charterType) {
      // Set the useType to the specific charter type
      loan.useType = changed.charterType;
    }

    if (values.useType === UseType.liveAboard || values.useType === UseType.fullTimer) {
      Modal.error({
        title: 'Important Information',
        content: (
          <>
            {loan.purpose === LoanPurpose.boat && (
              <p>
                We're sorry, at this time we do not offer marine loans for Live-aboards. Thank you.
              </p>
            )}
            {loan.purpose === LoanPurpose.rv && (
              <p>We're sorry, at this time we do not offer RV loans for Full-Timers. Thank you.</p>
            )}
          </>
        ),
      });

      values.useType = null;
    }
  };

  const ownershipTypeOptions: ReactElement[] = [];
  Object.values(OwnershipType).forEach((label) => {
    if (label === OwnershipType.montanaLlc && loan.purpose !== LoanPurpose.rv) return;
    ownershipTypeOptions.push(
      <Select.Option key={label} value={label}>
        {label}
      </Select.Option>
    );
  });

  const getUseTypeOptions = () => {
    const baseOptions = [
      { label: UseType.pleasure, value: UseType.pleasure },
      {
        label: loan.purpose === LoanPurpose.rv ? UseType.fullTimer : UseType.liveAboard,
        value: loan.purpose === LoanPurpose.rv ? UseType.fullTimer : UseType.liveAboard,
      },
    ];

    // CHARTER LOGIC: Show Charter option for boats when feature flag allows it
    if (loan.purpose === LoanPurpose.boat && SHOULD_SHOW_CHARTER_OPTION) {
      return [...baseOptions, { label: UseType.charter, value: UseType.charter }];
    }

    return baseOptions;
  };

  const validateUseType = (rule: any, value: any) => {
    if (!value) {
      return Promise.reject(new Error('Please select one'));
    }

    const unSupportedOptionErrorMessage = "Sorry, we don't currently support this option.";
    if (loan.purpose === LoanPurpose.rv && value === UseType.fullTimer) {
      return Promise.reject(new Error(unSupportedOptionErrorMessage));
    }

    if (loan.purpose === LoanPurpose.boat && value === UseType.liveAboard) {
      return Promise.reject(new Error(unSupportedOptionErrorMessage));
    }

    return Promise.resolve();
  };

  const validateCharterType = (rule: any, value: any) => {
    // Only validate if charter options are shown and no charter type is selected
    if (showCharterOptions && !value) {
      return Promise.reject(new Error('Please select a charter type'));
    }

    return Promise.resolve();
  };

  // Compute the display value for useType in the form
  // This shows "Charter" as selected when useType is a specific charter type, without overriding loan.useType
  // The actual loan.useType remains unchanged - this is only for UI display purposes
  const getDisplayUseType = () => {
    if (loan.useType === UseType.fullTimeCharter || loan.useType === UseType.partTimeCharter) {
      return UseType.charter;
    }
    return loan.useType;
  };

  const initialValues = {
    ...loan,
    // Use the computed display value for useType in the form
    useType: getDisplayUseType(),
    // Set charterType to the actual charter type if it exists
    charterType:
      loan.useType === UseType.fullTimeCharter || loan.useType === UseType.partTimeCharter
        ? loan.useType
        : undefined,
  };

  return (
    <LoanLayout>
      {isNewFunnel && <div className="step-title">Boat info</div>}
      <h1>
        {loan?.purpose === LoanPurpose.boat
          ? 'Apply for a loan in less than 5 minutes'
          : 'A few questions about your loan:'}
      </h1>
      {loan?.purpose === LoanPurpose.boat && <p>Tell us about the boat you'd like to finance</p>}
      <Row>
        <Col {...centerSmallOffset} />
        <Col {...centerSmall}>
          <Form
            form={form}
            initialValues={initialValues}
            onFinish={update}
            onValuesChange={handleChange}
          >
            <AppFormItem
              className="manual-label loan-type"
              name="loanType"
              required
              rules={[schemaValidator('loanType', loan, { message: 'Please select one' })]}
            >
              <PlaceholderLabel label="What type of loan are you interested in?" htmlFor="loanType">
                <Select
                  suffixIcon={<SelectArrowIcon />}
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  <Select.Option key={LoanType.new} value={LoanType.new}>
                    New Purchase
                  </Select.Option>
                  <Select.Option key={LoanType.used} value={LoanType.used}>
                    Used Purchase
                  </Select.Option>
                  <Select.Option key={LoanType.refinance} value={LoanType.refinance}>
                    Refinance
                  </Select.Option>
                  <Select.Option key={LoanType.cashRecapture} value={LoanType.cashRecapture}>
                    Cash Recapture/Cash Out
                  </Select.Option>
                </Select>
              </PlaceholderLabel>
            </AppFormItem>

            {loan.purpose !== LoanPurpose.aircraft && (
              <AppFormItem
                className="manual-label"
                name="useType"
                hasFeedback={false}
                required
                rules={[{ validator: validateUseType }]}
              >
                <ManualLabel twoLine label={`Will this ${loan.purpose} be used for:`}>
                  <Radio.Group optionType="button" options={getUseTypeOptions()} />
                </ManualLabel>
              </AppFormItem>
            )}

            {showCharterOptions && loan.purpose === LoanPurpose.boat && (
              <AppFormItem
                className="manual-label"
                name="charterType"
                hasFeedback={false}
                rules={[
                  {
                    validator: validateCharterType,
                  },
                ]}
              >
                <ManualLabel twoLine label="What type of charter?">
                  <Radio.Group
                    optionType="button"
                    options={[
                      { label: UseType.fullTimeCharter, value: UseType.fullTimeCharter },
                      { label: UseType.partTimeCharter, value: UseType.partTimeCharter },
                    ]}
                  />
                </ManualLabel>
              </AppFormItem>
            )}

            {showSubsourceDropdown && (
              <AppFormItem
                name="subSource"
                rules={[schemaValidator('subSource', loan, { transform: 'int' })]}
                hasFeedback
              >
                <PlaceholderLabel label="How did you hear about us?" htmlFor="subSource">
                  <Select
                    suffixIcon={<SelectArrowIcon />}
                    showSearch={subSources?.length > 7}
                    allowClear
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {subSources.map((s) => (
                      <Select.Option key={String(s.id)} value={s.id}>
                        {s.label || s.name}
                      </Select.Option>
                    ))}
                  </Select>
                </PlaceholderLabel>
              </AppFormItem>
            )}

            {loan.subSource === 5 && (
              <AppFormItem
                name="subSourceName"
                rules={[schemaValidator('subSourceName', loan)]}
                hasFeedback
              >
                <PlaceholderLabel label="How you heard of us? (optional)" htmlFor="subSourceName">
                  <Input />
                </PlaceholderLabel>
              </AppFormItem>
            )}

            {loan.purpose !== LoanPurpose.boat && loan.promocodeEditable && (
              <AppFormItem
                name="promocode"
                rules={[schemaValidator('promocode', loan)]}
                hasFeedback
              >
                <PlaceholderLabel label="Promocode (optional)" htmlFor="promocode">
                  <Input />
                </PlaceholderLabel>
              </AppFormItem>
            )}

            {loan.showMemberField && (
              <AppFormItem name="member" rules={[schemaValidator('member', loan)]} hasFeedback>
                <PlaceholderLabel label="Member Number" htmlFor="member">
                  <Input />
                </PlaceholderLabel>
              </AppFormItem>
            )}

            <ButtonNav />
            <StepFooter type="info" />
          </Form>
        </Col>
      </Row>
    </LoanLayout>
  );
});

export default DetailsStep;
